import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { usePaywall } from '@/hooks/usePaywall';
import type { PaywallFeature } from '@/store/paywall.store';
import { cn } from '@/lib/cn';

export interface PaywalledSectionProps {
  feature: PaywallFeature;
  /** Pill copy on the centred unlock CTA. Defaults to "Pick a plan to unlock". */
  ctaLabel?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Wraps a block of content so it remains visible (cards still show, the
 * reader can see what they're missing) but every interaction with the
 * children is captured and routed to the paywall modal. Children render
 * with `pointer-events: none` and a slightly desaturated tint; a single
 * absolute overlay button sits on top, listens for clicks/keyboard, and
 * fires `openPaywall(feature)`.
 *
 * No-op passthrough for paid users and admins.
 */
export function PaywalledSection({
  feature,
  ctaLabel,
  children,
  className,
}: PaywalledSectionProps) {
  const { isPaywalled, openPaywall } = usePaywall();
  if (!isPaywalled) return <>{children}</>;

  return (
    <div className={cn('relative', className)}>
      <div
        aria-hidden
        className="pointer-events-none select-none opacity-90"
        style={{ filter: 'grayscale(0.15)' }}
      >
        {children}
      </div>
      <button
        type="button"
        onClick={() => openPaywall(feature)}
        aria-label="Locked — pick a plan to unlock this feature"
        className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/0 transition hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-4 py-2 text-[13px] font-semibold text-amber-800 shadow-or-card">
          <Lock className="h-4 w-4" aria-hidden />
          {ctaLabel ?? 'Pick a plan to unlock'}
        </span>
      </button>
    </div>
  );
}
